// "Did last night's audience pull actually land?" — the PURE decision behind the
// demographics freshness note. LEAF (types only), `today` injected, no clock read.
//
// ⚠️⚠️ WHY THIS EXISTS. On 2026-08-31 the nightly demographics job had been
// failing on EVERY fire — its Cloud Scheduler was created without
// `roles/run.invoker`, so each attempt 403'd. Nothing anywhere reported it. The
// only signal in the whole system was a human noticing that one club's audience
// panel had gone missing, four rounds of investigation later.
//
// ⚠️⚠️ IT REPORTS THE AGE OF THE DATA, NOT OF THE LAST RUN — and that distinction
// is the whole point. A "last run succeeded" signal would have shown GREEN
// throughout, because the runs that mattered never started at all; and a job that
// runs and writes nothing is the same failure wearing a better hat. So the signal
// is the newest day that actually carries a DAILY demographics row: the thing the
// job exists to produce.
//
// ⚠️ THE AGE IS DERIVED AT THE DISPLAY EDGE, NOT ON THE SERVER, which is why the
// wire carries a DATE rather than an `hoursStale` the way `ReportingDataFreshness`
// does. "How many days behind" is measured against the VIEWER'S local today, and
// Cloud Run's clock is UTC — computing it server-side would report a Brisbane
// reader a day out every morning before 10:00, which is the exact class of bug
// `computePullWindow` was written to close.
//
// ⚠️ META ONLY, like `pullFreshness.ts`. This is the demographics pass's own
// cadence; Google's transfer knows nothing about it.

/**
 * ⚠️ THE WARNING THRESHOLD, IN DAYS, AND IT ALLOWS EXACTLY ONE FAILED NIGHT.
 *
 * A healthy nightly job leaves the newest daily row on YESTERDAY — one day
 * behind — so one is the normal, quiet state. Two means a single night was
 * missed, which happens and recovers on its own. Three or more is a job that is
 * not running, and by then something has to say so.
 *
 * ⚠️ Days rather than hours, deliberately: the data is day-grained, so "36 hours
 * behind" is not a quantity these rows can express. `> 2` is the brief's "more
 * than 48 hours behind", read in the units the warehouse actually has.
 */
export const DEMOGRAPHICS_STALE_DAYS = 2;

/** What the server knows: the newest day with a DAILY row, roster-wide. */
export interface ReportingDemographicsFreshness {
  /**
   * YYYY-MM-DD, or null when no daily demographics row exists at all.
   *
   * ⚠️ Roster-wide and DELIBERATELY unscoped. Whether last night's pass ran is a
   * property of the pipeline, not of one account — the same reasoning as the
   * Meta pull's `resolveDataFreshness`. A per-account freshness is a different,
   * much larger feature.
   */
  latestDailyDay: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whole days from `from` to `to`. Null when either is unparseable — never 0,
 * which would read as "bang up to date".
 */
export function daysBetween(from: string, to: string): number | null {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / DAY_MS);
}

export type DemographicsFreshnessNotice =
  | { kind: 'fresh'; latestDailyDay: string; daysBehind: number }
  | { kind: 'stale'; latestDailyDay: string; daysBehind: number }
  | { kind: 'never' }
  | { kind: 'unavailable' };

/**
 * Whether to say anything about the audience pull, and what.
 *
 * ⚠️ THREE UNKNOWNS, DELIBERATELY NOT COLLAPSED — the `pullFreshness` posture.
 * "The pass has never landed a daily row" is a SETUP state (the nightly job is
 * new, and its scheduler is an operator step); "it is three days behind" is an
 * OUTAGE; "we could not read the telemetry" is a transient read failure over
 * data that may be perfectly current. One sentence covering all three would
 * either cry wolf or understate a real outage.
 *
 * ⚠️ Returns null while the read is still in flight. A "we don't know" flash on
 * every page load trains people to ignore the line that matters.
 */
export function resolveDemographicsFreshnessNotice(args: {
  today: string;
  /** `undefined` = not loaded yet. A loaded null `latestDailyDay` is a real answer. */
  freshness: ReportingDemographicsFreshness | undefined;
  unavailable: boolean;
}): DemographicsFreshnessNotice | null {
  const { today, freshness, unavailable } = args;
  if (unavailable) return { kind: 'unavailable' };
  if (freshness === undefined) return null;
  const day = freshness.latestDailyDay;
  if (day === null) return { kind: 'never' };
  const daysBehind = daysBetween(day, today);
  // ⚠️ An unreadable date is NOT reported as fresh. A stamp we cannot parse is a
  // freshness claim we cannot make, and a wrong one is worse than none.
  if (daysBehind === null) return { kind: 'unavailable' };
  return daysBehind > DEMOGRAPHICS_STALE_DAYS
    ? { kind: 'stale', latestDailyDay: day, daysBehind }
    : { kind: 'fresh', latestDailyDay: day, daysBehind };
}

/**
 * The sentence itself.
 *
 * ⚠️ PURE, and deliberately not left in the component — the `pullFreshnessMessage`
 * / `conversionNote` precedent. The wording of an honesty caveat is the part
 * worth pinning, and pinning it here costs no jsdom (which this suite cannot
 * comfortably afford another of).
 */
export function demographicsFreshnessMessage(
  notice: DemographicsFreshnessNotice,
  formatDate: (iso: string) => string,
): string {
  if (notice.kind === 'unavailable') {
    return "couldn't read when audience data last landed, so nothing here says how current it is.";
  }
  if (notice.kind === 'never') {
    return (
      'no day-by-day audience data has ever landed. the nightly demographics pass writes it, ' +
      'and it needs a scheduler — until one exists, audience figures come from whatever wide ' +
      'pull windows happen to be stored.'
    );
  }
  const when = `day-by-day audience data is current to ${formatDate(notice.latestDailyDay)}`;
  if (notice.kind === 'fresh') {
    return `${when}.`;
  }
  // ⚠️ It names the JOB, because the reader's next question is "what do I do".
  // A bare "the data is old" points at nobody.
  return (
    `${when} — ${notice.daysBehind} days behind. the nightly demographics pass has not landed ` +
    'since then, so audience figures will be answering about an older period than the rest of ' +
    'this page. worth checking the job before reading anything into them.'
  );
}
